import {
    Table3Ref,
    useTableDataAllFetcher,
    useTableDataLoader,
    useTableDataLoaderOptions,
    useTableDataPageFetcher,
} from '@economic/taco';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Table3DataContext } from '~/app/shared/table3tools/contexts/Table3DataContext';

import { PaginatedDataFetcher } from '~/app/shared/table3tools/types';

export default function useTable3DataContextLoader<T>(
    fetcher: PaginatedDataFetcher<T>,
    options?: useTableDataLoaderOptions
): Table3DataContext<T> {
    const fetchPage: useTableDataPageFetcher<T> = useCallback(
        async (pageIndex, pageSize, sorting, filters) =>
            fetcher(
                {
                    pageIndex,
                    pageSize,
                },
                sorting,
                filters
            ),
        [fetcher]
    );

    const fetchAll: useTableDataAllFetcher<T> = useCallback(
        async (sorting, filters) => fetcher(undefined, sorting, filters),
        [fetcher]
    );

    const [{ data, length, loadAll, loadPage, onChangeFilter, onChangeSort, onChangeSearch, pageSize }, invalidate] =
        useTableDataLoader<T>(fetchPage, fetchAll, options);

    const tableRef = React.useRef<Table3Ref>(null);
    const reload = useCallback(() => {
        invalidate();
        tableRef.current?.instance?.resetRowSelection();
    }, [invalidate]);

    const context = useMemo<Table3DataContext<T>>(
        () => ({
            data,
            tableRef,
            length,
            loadAll,
            loadPage,
            onChangeFilter,
            onChangeSort,
            onChangeSearch,
            pageSize,
            reload,
            hasInitialized: length !== undefined,
        }),
        [data, length, loadAll, loadPage, onChangeFilter, onChangeSort, onChangeSearch, pageSize, tableRef, reload]
    );

    // since Table3 does not invalidate its own data when the fetcher changes we have to invalidate it manually,
    useEffect(() => {
        // only reload if we have actually initialized
        if (context.hasInitialized) {
            context.reload();
        }
        // but we cannot depend on the values returned from the useTableDataLoader directly in this useEffect
        // since they're neither stable nor dependent on the fetcher changing
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetcher]);

    return context;
}
